// SPDX-License-Identifier: AGPL-3.0-or-later

import * as GuildBoostCommands from '@app/features/guild/commands/GuildBoostCommands';
import * as GuildCommands from '@app/features/guild/commands/GuildCommands';
import {showGuildErrorModal} from '@app/features/guild/components/alerts/GuildErrorModalUtils';
import {GuildBoostIntegratedProgress} from '@app/features/guild/components/GuildBoostIntegratedProgress';
import {GuildBoostConfirmModal} from '@app/features/guild/components/modals/guild_boost/GuildBoostConfirmModal';
import styles from '@app/features/guild/components/modals/guild_tabs/GuildBoostTab.module.css';
import {GuildIcon} from '@app/features/guild/components/popouts/GuildIcon';
import Guilds from '@app/features/guild/state/Guilds';
import {
	getGuildBoostFeatureLabel,
	getGuildBoostStickerSlotsLabel,
} from '@app/features/guild/utils/GuildBoostFeatureLabels';
import {
	GUILD_BOOST_APPLY_CTA_DESCRIPTOR,
	GUILD_BOOST_BOOSTERS_SECTION_DESCRIPTOR,
	GUILD_BOOST_COUNT_DESCRIPTOR,
	GUILD_BOOST_LEVEL_BADGE_DESCRIPTOR,
	GUILD_BOOST_LEVELS_SECTION_DESCRIPTOR,
	GUILD_BOOST_LOAD_FAILED_INLINE_DESCRIPTOR,
	GUILD_BOOST_LOAD_FAILED_TITLE_DESCRIPTOR,
	GUILD_BOOST_NO_BOOSTERS_DESCRIPTOR,
	GUILD_BOOST_PREMIUM_REQUIRED_DESCRIPTOR,
	GUILD_BOOST_PREMIUM_SLOTS_USAGE_DESCRIPTOR,
	GUILD_BOOST_PROGRESS_BAR_TOGGLE_DESCRIPTION_DESCRIPTOR,
	GUILD_BOOST_PROGRESS_BAR_TOGGLE_LABEL_DESCRIPTOR,
	GUILD_BOOST_PROGRESS_BAR_TOGGLE_SECTION_DESCRIPTOR,
	GUILD_BOOST_SLOTS_SHORT_DESCRIPTOR,
	GUILD_BOOST_YOUR_SLOTS_SECTION_DESCRIPTOR,
} from '@app/features/guild/utils/GuildBoostMessageDescriptors';
import {TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import Permission from '@app/features/permissions/state/Permission';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {BaseAvatar} from '@app/features/ui/components/BaseAvatar';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {Spinner} from '@app/features/ui/components/Spinner';
import Users from '@app/features/user/state/Users';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {getFormattedShortDate} from '@app/features/user/utils/DateFormatting';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {
	getGuildBoostFeaturesIntroducedAtLevel,
	GUILD_BOOST_SLOTS_PER_PREMIUM_USER,
} from '@fluxer/constants/src/GuildBoostConstants';
import {GuildFeatures} from '@fluxer/constants/src/GuildConstants';
import type {GuildBoostStatusResponse} from '@fluxer/schema/src/domains/guild/GuildBoostSchemas';
import {useLingui} from '@lingui/react/macro';
import {WarningIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';

const logger = new Logger('GuildBoostTab');

function computeBoostProgress(status: GuildBoostStatusResponse): {ratio: number; label: string} {
	const count = status.premium_subscription_count;
	if (status.next_level_requires == null) {
		return {ratio: 1, label: 'max'};
	}
	const currentRequired =
		status.premium_tier === 0
			? 0
			: (status.levels.find((level) => level.level === status.premium_tier)?.required ?? 0);
	const nextRequired = status.next_level_requires;
	const span = nextRequired - currentRequired;
	if (span <= 0) {
		return {ratio: 1, label: 'full'};
	}
	return {
		ratio: Math.min(1, Math.max(0, (count - currentRequired) / span)),
		label: 'progress',
	};
}

const GuildBoostTab: React.FC<{guildId: string}> = observer(({guildId}) => {
	const {i18n} = useLingui();
	const guild = Guilds.getGuild(guildId);
	const [status, setStatus] = useState<GuildBoostStatusResponse | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isTogglingProgressBar, setIsTogglingProgressBar] = useState(false);
	const [loadError, setLoadError] = useState(false);
	const canManageGuild = Permission.can(Permissions.MANAGE_GUILD, {guildId});
	const progressBarEnabled = guild?.features.has(GuildFeatures.PREMIUM_PROGRESS_BAR_ENABLED) ?? false;

	const reloadStatus = useCallback(async () => {
		setIsLoading(true);
		setLoadError(false);
		try {
			const next = await GuildBoostCommands.fetchGuildBoostStatus(guildId);
			setStatus(next);
			GuildBoostCommands.syncGuildBoostStatusToStore(guildId, next);
		} catch (error) {
			logger.error('Failed to load guild boost status', error);
			setLoadError(true);
			showGuildErrorModal({
				title: i18n._(GUILD_BOOST_LOAD_FAILED_TITLE_DESCRIPTOR),
				message: i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR),
				dataFlx: 'guild.guild-tabs.guild-boost-tab.load-error-modal',
			});
		} finally {
			setIsLoading(false);
		}
	}, [guildId, i18n]);

	useEffect(() => {
		void reloadStatus();
	}, [reloadStatus]);

	const progress = useMemo(() => (status ? computeBoostProgress(status) : null), [status]);

	const groupedBoosters = useMemo(() => {
		if (!status) return [];
		const byUser = new Map<
			string,
			{user: (typeof status.boosters)[number]['user']; count: number; created_at: string}
		>();
		for (const booster of status.boosters) {
			const existing = byUser.get(booster.user.id);
			if (existing) {
				existing.count += 1;
				if (booster.created_at < existing.created_at) {
					existing.created_at = booster.created_at;
				}
			} else {
				byUser.set(booster.user.id, {
					user: booster.user,
					count: 1,
					created_at: booster.created_at,
				});
			}
		}
		return Array.from(byUser.values()).sort(
			(a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
		);
	}, [status]);

	const handleBoost = useCallback(() => {
		if (!status?.me.can_boost || !guild) return;
		ModalCommands.push(
			modal(() => (
				<GuildBoostConfirmModal
					guildId={guildId}
					guildName={guild.name}
					initialStatus={status}
					onApplied={setStatus}
					data-flx="guild.guild-tabs.guild-boost-tab.confirm-modal"
				/>
			)),
		);
	}, [guild, guildId, status]);

	const handleProgressBarToggle = useCallback(
		async (enabled: boolean) => {
			if (!canManageGuild || isTogglingProgressBar) return;
			setIsTogglingProgressBar(true);
			try {
				await GuildCommands.toggleFeature(guildId, GuildFeatures.PREMIUM_PROGRESS_BAR_ENABLED, enabled);
			} catch (error) {
				logger.error('Failed to toggle boost progress bar', error);
				showGuildErrorModal({
					title: i18n._(GUILD_BOOST_LOAD_FAILED_TITLE_DESCRIPTOR),
					message: i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR),
					dataFlx: 'guild.guild-tabs.guild-boost-tab.progress-bar-toggle-error-modal',
				});
			} finally {
				setIsTogglingProgressBar(false);
			}
		},
		[canManageGuild, guildId, i18n, isTogglingProgressBar],
	);

	if (isLoading && !status) {
		return (
			<div className={styles.spinnerContainer} data-flx="guild.guild-tabs.guild-boost-tab.spinner-container">
				<Spinner data-flx="guild.guild-tabs.guild-boost-tab.spinner" />
			</div>
		);
	}

	if (!guild || !status) {
		return loadError ? (
			<div className={styles.errorAlert} role="alert" data-flx="guild.guild-tabs.guild-boost-tab.error-alert">
				<WarningIcon size={remFromPx(16)} weight="fill" />
				<span>{i18n._(GUILD_BOOST_LOAD_FAILED_INLINE_DESCRIPTOR)}</span>
			</div>
		) : null;
	}

	return (
		<div className={styles.container} data-flx="guild.guild-tabs.guild-boost-tab.container">
			<section className={styles.hero} data-flx="guild.guild-tabs.guild-boost-tab.hero">
				<div className={styles.heroTop} data-flx="guild.guild-tabs.guild-boost-tab.hero-top">
					<div className={styles.heroIcon} data-flx="guild.guild-tabs.guild-boost-tab.hero-icon">
						<GuildIcon id={guild.id} name={guild.name} icon={guild.icon} sizePx={56} />
					</div>
					<div className={styles.heroMeta} data-flx="guild.guild-tabs.guild-boost-tab.hero-meta">
						<div className={styles.heroGuildName} data-flx="guild.guild-tabs.guild-boost-tab.hero-guild-name">
							{guild.name}
						</div>
						<GuildBoostIntegratedProgress
							level={status.premium_tier}
							count={status.premium_subscription_count}
							nextRequired={status.next_level_requires}
							isMaxLevel={status.next_level_requires == null}
							ratio={progress?.ratio ?? 0}
							variant="relaxed"
							className={styles.heroProgress}
							data-flx="guild.guild-tabs.guild-boost-tab.hero-progress"
						/>
					</div>
				</div>
			</section>

			<div className={styles.meCard} data-flx="guild.guild-tabs.guild-boost-tab.progress-bar-card">
				<h3 className={styles.sectionTitle} data-flx="guild.guild-tabs.guild-boost-tab.progress-bar-title">
					{i18n._(GUILD_BOOST_PROGRESS_BAR_TOGGLE_SECTION_DESCRIPTOR)}
				</h3>
				<Switch
					value={progressBarEnabled}
					onChange={(enabled) => void handleProgressBarToggle(enabled)}
					disabled={!canManageGuild || isTogglingProgressBar}
					label={i18n._(GUILD_BOOST_PROGRESS_BAR_TOGGLE_LABEL_DESCRIPTOR)}
					description={i18n._(GUILD_BOOST_PROGRESS_BAR_TOGGLE_DESCRIPTION_DESCRIPTOR)}
					data-flx="guild.guild-tabs.guild-boost-tab.progress-bar-switch"
				/>
			</div>

			<div data-flx="guild.guild-tabs.guild-boost-tab.levels-section">
				<h3 className={styles.sectionTitle} data-flx="guild.guild-tabs.guild-boost-tab.levels-title">
					{i18n._(GUILD_BOOST_LEVELS_SECTION_DESCRIPTOR)}
				</h3>
				<div className={styles.levelGrid} data-flx="guild.guild-tabs.guild-boost-tab.level-grid">
					{status.levels.map((levelInfo) => {
						const introducedFeatures = getGuildBoostFeaturesIntroducedAtLevel(levelInfo.level);
						const isCurrentLevel = status.premium_tier === levelInfo.level;
						return (
							<div
								key={levelInfo.level}
								className={clsx(
									styles.levelCard,
									levelInfo.unlocked && styles.levelCardUnlocked,
									isCurrentLevel && styles.levelCardCurrent,
								)}
								data-flx={`guild.guild-tabs.guild-boost-tab.level-card.${levelInfo.level}`}
							>
								<div className={styles.levelCardHeader}>
									<span className={styles.levelCardTitle}>
										{i18n._(GUILD_BOOST_LEVEL_BADGE_DESCRIPTOR, {level: levelInfo.level})}
									</span>
									<span className={styles.levelCardRequirement}>
										{i18n._(GUILD_BOOST_COUNT_DESCRIPTOR, {count: levelInfo.required})}
									</span>
								</div>
								<ul className={styles.perkList}>
									{introducedFeatures.map((feature) => (
										<li key={feature} className={styles.perkItem}>
											<span className={styles.perkDot} />
											{getGuildBoostFeatureLabel(i18n, feature)}
										</li>
									))}
									<li className={styles.perkItem}>
										<span className={styles.perkDot} />
										{getGuildBoostStickerSlotsLabel(i18n, levelInfo.sticker_slots)}
									</li>
								</ul>
							</div>
						);
					})}
				</div>
			</div>

			<div className={styles.meCard} data-flx="guild.guild-tabs.guild-boost-tab.me-card">
				<h3 className={styles.sectionTitle} data-flx="guild.guild-tabs.guild-boost-tab.me-title">
					{i18n._(GUILD_BOOST_YOUR_SLOTS_SECTION_DESCRIPTOR)}
				</h3>
				<div className={styles.meRow} data-flx="guild.guild-tabs.guild-boost-tab.me-row">
					<div className={styles.meStats} data-flx="guild.guild-tabs.guild-boost-tab.me-stats">
						<span>
							{i18n._(GUILD_BOOST_SLOTS_SHORT_DESCRIPTOR, {
								available: status.me.slots_available,
								total: status.me.slots_total,
							})}
						</span>
						<span className={styles.meStatMuted}>
							{status.me.is_premium
								? i18n._(GUILD_BOOST_PREMIUM_SLOTS_USAGE_DESCRIPTOR, {
										perPremium: GUILD_BOOST_SLOTS_PER_PREMIUM_USER,
										usedOnGuild: status.me.slots_used_on_this_guild,
									})
								: i18n._(GUILD_BOOST_PREMIUM_REQUIRED_DESCRIPTOR)}
						</span>
					</div>
					<div data-flx="guild.guild-tabs.guild-boost-tab.me-actions">
						{status.me.can_boost && (
							<Button onClick={handleBoost} disabled={isSubmitting} data-flx="guild.guild-tabs.guild-boost-tab.boost-button">
								{i18n._(GUILD_BOOST_APPLY_CTA_DESCRIPTOR)}
							</Button>
						)}
					</div>
				</div>
			</div>

			<div className={styles.boostersCard} data-flx="guild.guild-tabs.guild-boost-tab.boosters-card">
				<h3 className={styles.sectionTitle} data-flx="guild.guild-tabs.guild-boost-tab.boosters-title">
					{i18n._(GUILD_BOOST_BOOSTERS_SECTION_DESCRIPTOR)}
				</h3>
				{groupedBoosters.length === 0 ? (
					<p className={styles.emptyBoosters} data-flx="guild.guild-tabs.guild-boost-tab.empty-boosters">
						{i18n._(GUILD_BOOST_NO_BOOSTERS_DESCRIPTOR)}
					</p>
				) : (
					<div className={styles.boosterList} data-flx="guild.guild-tabs.guild-boost-tab.booster-list">
						{groupedBoosters.map((booster) => {
							const cachedUser = Users.getUser(booster.user.id);
							const avatarUrl = AvatarUtils.getUserAvatarURL({
								id: booster.user.id,
								avatar: cachedUser?.avatar ?? booster.user.avatar,
							});
							return (
								<div
									key={booster.user.id}
									className={styles.boosterRow}
									data-flx="guild.guild-tabs.guild-boost-tab.booster-row"
								>
									<div className={styles.boosterAvatar} data-flx="guild.guild-tabs.guild-boost-tab.booster-avatar">
										<BaseAvatar
											size={32}
											avatarUrl={avatarUrl}
											userTag={
												cachedUser?.tag ?? `${booster.user.username}#${booster.user.discriminator}`
											}
											showOffline={false}
											data-flx="guild.guild-tabs.guild-boost-tab.booster-base-avatar"
										/>
									</div>
									<div className={styles.boosterMeta} data-flx="guild.guild-tabs.guild-boost-tab.booster-meta">
										<span className={styles.boosterNameRow}>
											<span className={styles.boosterName}>
												{NicknameUtils.getDisplayName(cachedUser ?? booster.user)}
											</span>
											{booster.count > 1 && (
												<span className={styles.boosterCount}>
													{i18n._(GUILD_BOOST_COUNT_DESCRIPTOR, {count: booster.count})}
												</span>
											)}
										</span>
										<span className={styles.boosterDate}>{getFormattedShortDate(booster.created_at)}</span>
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
});

export default GuildBoostTab;
